import ApplicationController from './application_controller'

console.log('🛒 OpportunityCartController loaded successfully')

/**
 * OpportunityCartController
 *
 * Manages inline quantity controls for purchasable items (tickets, store items, packages)
 * on the opportunity page. Provides add/increment/decrement functionality with
 * optimistic UI updates and server synchronization.
 */
export default class extends ApplicationController {
  static targets = [
    'quantityDisplay',        // Span showing current quantity (in cart)
    'addButton',              // "Add to Cart" button
    'quantityControls',       // Container with +/- buttons (shown when qty > 0)
    'incrementButton',        // (+) button for in-cart
    'decrementButton',        // (-) button for in-cart
    'preCartControls',        // Container for pre-add quantity selector
    'preIncrementButton',     // (+) button for pending quantity
    'preDecrementButton',     // (-) button for pending quantity
    'pendingQuantityDisplay', // Span showing pending quantity before add
    'loadingIndicator',       // Optional spinner during AJAX
    'itemTotal'               // Optional line total display
  ]

  static values = {
    itemType: String,           // 'ticket', 'store_item', or 'package'
    itemId: String,             // ID of the TicketType/StoreItem/Package
    opportunityId: String,      // Opportunity ID
    cartItemId: String,         // ShoppingCartItem ID (if already in cart)
    unitPrice: Number,          // For client-side total calculation
    maxQuantity: Number,
    currentQuantity: Number,
    pendingQuantity: Number,    // Quantity to add (before clicking Add to Cart)
    setQuantityUrl: String      // POST URL for setting quantity
  }

  connect() {
    super.connect()
    // Set defaults for Stimulus v2 (doesn't support default values in static values)
    if (!this.hasMaxQuantityValue) this.maxQuantityValue = 999
    if (!this.hasCurrentQuantityValue) this.currentQuantityValue = 0
    if (!this.hasPendingQuantityValue) this.pendingQuantityValue = 1
    this.updateUI()
  }

  /**
   * Increment pending quantity (before adding to cart)
   */
  incrementPending(event) {
    event.preventDefault()
    if (this.pendingQuantityValue < this.maxQuantityValue) {
      this.pendingQuantityValue++
      this.updatePendingUI()
    }
  }

  /**
   * Decrement pending quantity (before adding to cart)
   */
  decrementPending(event) {
    event.preventDefault()
    if (this.pendingQuantityValue > 1) {
      this.pendingQuantityValue--
      this.updatePendingUI()
    }
  }

  /**
   * Update the pending quantity display and button states
   */
  updatePendingUI() {
    if (this.hasPendingQuantityDisplayTarget) {
      this.pendingQuantityDisplayTarget.textContent = this.pendingQuantityValue
    }

    if (this.hasPreDecrementButtonTarget) {
      this.preDecrementButtonTarget.disabled = this.pendingQuantityValue <= 1
    }

    if (this.hasPreIncrementButtonTarget) {
      this.preIncrementButtonTarget.disabled = this.pendingQuantityValue >= this.maxQuantityValue
    }
  }

  /**
   * Add items to cart with the pending quantity
   */
  addToCart(event) {
    event.preventDefault()
    const quantity = this.pendingQuantityValue || 1
    this.updateQuantity(quantity)
  }

  /**
   * Increase quantity by 1
   */
  increment(event) {
    event.preventDefault()
    const newQuantity = this.currentQuantityValue + 1

    if (newQuantity <= this.maxQuantityValue) {
      this.updateQuantity(newQuantity)
    } else {
      this.showError(`Maximum ${this.maxQuantityValue} allowed`)
    }
  }

  /**
   * Decrease quantity by 1 (removes item if going to 0)
   */
  decrement(event) {
    event.preventDefault()
    const newQuantity = this.currentQuantityValue - 1

    if (newQuantity >= 0) {
      this.updateQuantity(newQuantity)
    }
  }

  /**
   * Core method that handles add/update/remove via the set_quantity endpoint
   */
  updateQuantity(quantity) {
    const previousQuantity = this.currentQuantityValue

    // Optimistic UI update
    this.currentQuantityValue = quantity
    this.updateUI()
    this.setLoading(true)

    // Build the URL - use the set_quantity endpoint
    const url = this.setQuantityUrlValue || '/shopping_cart/set_quantity'

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        opportunity_id: this.opportunityIdValue,
        item_type: this.itemTypeValue,
        item_id: this.itemIdValue,
        quantity: quantity
      })
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || 'Failed to update cart')
        })
      }
      return response.json()
    })
    .then(data => {
      if (data.success) {
        // Update cart item ID if we just added a new item
        if (data.cart && data.cart.items) {
          const purchasableType = this.getPurchasableType()
          const item = data.cart.items.find(i =>
            i.purchasable_type === purchasableType &&
            String(i.purchasable_id) === String(this.itemIdValue)
          )
          if (item) {
            this.cartItemIdValue = String(item.id)
          }
        }

        // Dispatch cart updated event for other components (header badge, checkout CTA)
        this.dispatchCartUpdated(data)

        // Show success feedback
        this.showSuccess(data.message)
      } else {
        // Revert on failure
        this.currentQuantityValue = previousQuantity
        this.updateUI()
        this.showError(data.error || 'Failed to update cart')
      }
    })
    .catch(error => {
      console.error('Error updating cart:', error)
      // Revert on error
      this.currentQuantityValue = previousQuantity
      this.updateUI()
      this.showError(error.message || 'Failed to update cart')
    })
    .finally(() => {
      this.setLoading(false)
    })
  }

  /**
   * Update the UI based on current quantity
   */
  updateUI() {
    const qty = this.currentQuantityValue

    // Update quantity display (in-cart quantity)
    if (this.hasQuantityDisplayTarget) {
      this.quantityDisplayTarget.textContent = qty
    }

    // Toggle between pre-cart controls and in-cart controls
    if (qty === 0) {
      // Show pre-cart controls (quantity selector + Add button), hide in-cart controls
      if (this.hasPreCartControlsTarget) {
        this.preCartControlsTarget.classList.remove('d-none')
      }
      if (this.hasQuantityControlsTarget) {
        this.quantityControlsTarget.classList.add('d-none')
      }
      // Reset pending quantity to 1 when removed from cart
      this.pendingQuantityValue = 1
      this.updatePendingUI()
    } else {
      // Hide pre-cart controls, show in-cart controls
      if (this.hasPreCartControlsTarget) {
        this.preCartControlsTarget.classList.add('d-none')
      }
      if (this.hasQuantityControlsTarget) {
        this.quantityControlsTarget.classList.remove('d-none')
      }
    }

    // Disable decrement at 0 (though we hide controls at 0, this is a safety)
    if (this.hasDecrementButtonTarget) {
      this.decrementButtonTarget.disabled = qty <= 0
    }

    // Disable increment at max
    if (this.hasIncrementButtonTarget) {
      this.incrementButtonTarget.disabled = qty >= this.maxQuantityValue
    }

    // Update item total if displayed
    if (this.hasItemTotalTarget && this.unitPriceValue) {
      const total = qty * this.unitPriceValue
      this.itemTotalTarget.textContent = this.formatCurrency(total)
    }
  }

  /**
   * Show/hide loading state
   */
  setLoading(loading) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('d-none', !loading)
    }

    // Disable buttons during loading
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = loading
    }
    if (this.hasIncrementButtonTarget) {
      this.incrementButtonTarget.disabled = loading
    }
    if (this.hasDecrementButtonTarget) {
      this.decrementButtonTarget.disabled = loading
    }
  }

  /**
   * Dispatch custom event for other components to listen to
   */
  dispatchCartUpdated(data) {
    const event = new CustomEvent('cartUpdated', {
      bubbles: true,
      detail: {
        cart: data.cart,
        itemType: this.itemTypeValue,
        itemId: this.itemIdValue,
        quantity: this.currentQuantityValue
      }
    })
    document.dispatchEvent(event)
  }

  /**
   * Get the purchasable type string for matching
   */
  getPurchasableType() {
    switch (this.itemTypeValue) {
      case 'ticket': return 'TicketType'
      case 'store_item': return 'StoreItem'
      case 'package': return 'Package'
      default: return null
    }
  }

  /**
   * Show success message (brief feedback)
   */
  showSuccess(message) {
    // Add a brief visual feedback - pulse animation on quantity
    if (this.hasQuantityDisplayTarget) {
      this.quantityDisplayTarget.classList.add('quantity-updated')
      setTimeout(() => {
        this.quantityDisplayTarget.classList.remove('quantity-updated')
      }, 300)
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    // You could use a toast library here, for now just console and alert
    console.error('Cart error:', message)
    // Optionally show inline error
    const errorEl = this.element.querySelector('.cart-error')
    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.remove('d-none')
      setTimeout(() => errorEl.classList.add('d-none'), 3000)
    }
  }

  /**
   * Format number as currency
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  /**
   * Get CSRF token from meta tag
   */
  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
